/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module VrfContextModule
 */

/**
 * @description BGP Profile Message Item.
 *
 * @author Aravindh Nagarajan, Zhiqian Liu, Alex Klyuev
 */

import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';
import {
    RepeatedMessageItem,
} from 'ajs/modules/data-model/factories/repeated-message-item.factory';
import {
    withEditChildMessageItemMixin,
} from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';
import { CloudType, IBgpProfile } from 'generated-types';
import { BgpProfile } from 'object-types';
import { BgpRoutingOptionsConfigItem } from './bgp-routing-options.config-item.factory';
import { BgpPeerConfigItem } from './bgp-peer.config-item.factory';

type TBgpProfilePartial = Omit<IBgpProfile, 'peers' | 'routing_options'>;

interface IBgpProfileConfig extends TBgpProfilePartial {
    peers: RepeatedMessageItem<BgpPeerConfigItem>
    routing_options: RepeatedMessageItem<BgpRoutingOptionsConfigItem>;
    bgpType?: BgpTypes; // UI only property - to handle BGP type dropdown
}

export enum BgpTypes {
    ibgp = 'iBGP',
    ebgp = 'eBGP',
}

/**
 * Child fieldname constants.
 */
const PEERS = 'peers';
const ROUTING_OPTIONS = 'routing_options';

/**
 * @description BgpProfile messageItem class
 *
 * @author Aravindh Nagarajan.
 */
export class BgpProfileConfigItem extends
    withEditChildMessageItemMixin<IBgpProfileConfig, typeof MessageItem>(
        MessageItem,
    )<IBgpProfileConfig> {
    constructor(args = {}) {
        const extendedArgs = {
            objectType: BgpProfile,
            ...args,
        };

        super(extendedArgs);
    }

    /** @override */
    public modifyConfigDataAfterLoad(): void {
        super.modifyConfigDataAfterLoad();

        const {
            ibgp,
        } = this.config;

        this.type = ibgp ? BgpTypes.ibgp : BgpTypes.ebgp;
    }

    /** @override */
    public modifyConfigDataBeforeSave(): void {
        super.modifyConfigDataBeforeSave();

        this.config.ibgp = this.type === BgpTypes.ibgp;

        delete this.config.bgpType;
    }

    /**
     * Getter for BGP Type: iBGP/eBGP.
     */
    public get type(): BgpTypes {
        return this.config.bgpType;
    }

    /**
     * Setter for BGP Type: iBGP/eBGP.
     */
    public set type(bgpType: BgpTypes) {
        this.config.bgpType = bgpType;
    }

    /**
     * Getter for BGP Id.
     */
    public get bgpId(): number {
        return this.config.local_as;
    }

    /**
     * Returns true if bgpProfile has peers.
     */
    public hasPeers(): boolean {
        return !this.peers.isEmpty();
    }

    /**
     * Getter for Bgp peers.
     */
    public get peers(): RepeatedMessageItem<BgpPeerConfigItem> {
        const { peers } = this.config;

        return peers;
    }

    /**
     * Getter for Bgp RoutingOptions.
     */
    public get routingOptions(): RepeatedMessageItem<BgpRoutingOptionsConfigItem> {
        const { [ROUTING_OPTIONS]: routingOptions } = this.config;

        return routingOptions;
    }

    /**
     * Returns true if bgp is disabled.
     */
    public get isDisabled(): boolean {
        return !this.bgpId;
    }

    /**
     * Method to add a BGP Peer.
     */
    public addBgpPeer(
        vrfContextId: string,
        cloudId: string,
        cloudType: CloudType,
    ): void {
        this.addChildMessageItem({
            field: PEERS,
            modalBindings: {
                bgpProfileType: this.type,
                vrfContextId,
                cloudId,
                cloudType,
            },
        });
    }

    /**
     * Clear peers on BgpType Change.
     */
    public onBgpTypeChange(): void {
        this.peers.removeAll();
    }

    /**
     * Method to edit a BGP Peer.
     */
    public editBgpPeer(
        messageItem: BgpPeerConfigItem,
        vrfContextId: string,
        cloudId: string,
        cloudType: CloudType,
    ): void {
        const editArgs = {
            field: PEERS,
            messageItem,
            modalBindings: {
                bgpProfileType: this.type,
                vrfContextId,
                cloudId,
                cloudType,
            },
        };

        this.editChildMessageItem(editArgs);
    }

    /**
     * Method to create BGP Routing Options.
     */
    public addBgpRoutingOptions(): void {
        this.addChildMessageItem({
            field: ROUTING_OPTIONS,
        });
    }

    /**
     * Method to edit BGP Routing Options.
     */
    public editBgpRoutingOptions(messageItem: BgpRoutingOptionsConfigItem): void {
        this.editChildMessageItem({
            field: ROUTING_OPTIONS,
            messageItem,
        });
    }

    /**
     * Check if bgpType is iBGP.
     */
    public isIbgp(): boolean {
        const { type: bgpType } = this;

        return bgpType === BgpTypes.ibgp;
    }

    /**
     * Remove field from config as per the bgpType.
     */
    public removeFieldsByBgpType(): void {
        const {
            config,
            type: bgpType,
        } = this;

        if (bgpType !== BgpTypes.ibgp) {
            delete config.local_preference;
        } else {
            delete config.num_as_path_prepend;
        }
    }

    /** @override */
    protected requiredFields(): string[] {
        return [
            PEERS,
            ROUTING_OPTIONS,
        ];
    }
}
